import { ArrowsClockwise, Chat, ThumbsUp } from "@phosphor-icons/react";

import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import Switch from "common/components/ui/Switch";
import { formatNumber, typedObjectEntries } from "common/helpers/utils";

import LeadsFound from "../LeadsFound";
import PreviewItemRow from "../PreviewItemRow";

import type { LinkedInPost, PostLeadListSource } from "../../../../types";

export default function SelectedPost({
  post,
  isLoading,
  targetingParameters,
  setTargetingParameters,
}: {
  post: LinkedInPost;
  isLoading: boolean;
  targetingParameters: Omit<PostLeadListSource["settings"], "post_id" | "type">;
  setTargetingParameters: (
    updatedTargetingParameters: Omit<
      PostLeadListSource["settings"],
      "post_id" | "type"
    >,
  ) => void;
}) {
  const postStatuses = {
    likes: { icon: ThumbsUp, label: "Like", amount: post?.likes || 0 },
    comments: {
      icon: Chat,
      label: "Comment",
      amount: post?.comments || 0,
    },
    reposts: {
      icon: ArrowsClockwise,
      label: "Repost",
      amount: post?.shares || 0,
    },
  };
  const leadsFound = typedObjectEntries(postStatuses).reduce(
    (total, [, { amount }]) => total + amount,
    0,
  );

  let imageComponent = <Skeleton className="w-[232px] rounded-4xl h-[138px]" />;

  if (post?.images?.length && post.images[0].length) {
    imageComponent = (
      <div className="w-[232px] h-[138px] border rounded-4xl border-purple-100 bg-purple-10">
        <img
          src={post.images[0][0].url}
          className="h-full object-cover"
          alt=""
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-x-5 items-center">
        {imageComponent}
        <div className="flex flex-col py-1.5 gap-y-5">
          <div className="flex gap-2">
            {isLoading ? (
              <>
                <Skeleton className="size-10 shrink-0 rounded-full" />
                <div className="flex w-full flex-col gap-y-0.5">
                  <Skeleton className="h-5 w-30" />
                  <Skeleton className="h-4 w-22" />
                </div>
              </>
            ) : (
              <>
                <ProfileImage size="md" src={post.author.image.url} />
                <div className="flex flex-col gap-0.5">
                  <h5 className="text-sm-bold">{post.author.name}</h5>
                  <span className="line-clamp-1 text-black-400 text-xs-medium">
                    {post.author.subtitle}
                  </span>
                </div>
              </>
            )}
          </div>
          {isLoading ? (
            <div className="flex gap-y-0.5 flex-col">
              <Skeleton className="w-48 h-5" />
              <Skeleton className="w-48 h-5" />
              <Skeleton className="w-38 h-5" />
            </div>
          ) : (
            <p className="text-black-500 text-sm">{post.text}</p>
          )}
        </div>
      </div>
      <div className="flex flex-col">
        {typedObjectEntries(postStatuses).map(
          ([key, { icon: StatIcon, label, amount }]) => {
            const isChecked = targetingParameters[key];

            return (
              <PreviewItemRow key={key} icon={StatIcon} label={label}>
                <div className="flex bg-black-100 p-1.5 pr-3 items-center rounded-2xl gap-x-3">
                  {isLoading ? (
                    <Skeleton className="w-9 h-5" />
                  ) : (
                    <span className="text-button-sm w-9 text-center">
                      {formatNumber(amount)}
                    </span>
                  )}
                  <Switch
                    size="sm"
                    color="black"
                    checked={isChecked}
                    disabled={isLoading || !amount}
                    onCheckedChange={(checked) => {
                      setTargetingParameters({
                        ...targetingParameters,
                        [key]: checked,
                      });
                    }}
                  />
                </div>
              </PreviewItemRow>
            );
          },
        )}
      </div>
      <LeadsFound isLoading={isLoading}>{leadsFound}</LeadsFound>
    </>
  );
}
